<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRentalGeneralPanel',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    rental: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.rental,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rental');
    }
  );

  const act_2095089031469157_emit_66juyfxba_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_9ap0s0jtx_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_bkzf3skf2_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MRentalGeneralDataEditor',
      props: {
        rental: _.cloneDeep(props.rental),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_1lx2fzfg8 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_q1mknmhlm =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_1lx2fzfg8,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_q1mknmhlm],
    event: 'reload',
  });
  const act_2095089031469157_emit_66juyfxba = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_66juyfxba_getActionArgs,
  });
  const inst_evh_2095089031469157_close_9ap0s0jtx =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_66juyfxba,
      event: 'close',
      when: inst_evh_2095089031469157_close_9ap0s0jtx_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_bkzf3skf2 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_bkzf3skf2_getActionArgs,
      events: [inst_evh_2095089031469157_close_9ap0s0jtx],
    });
  const inst_evh_2095089031469157_clickPreventStop_nvj10oer9 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_bkzf3skf2,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_nvj10oer9],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_q1mknmhlm,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_9ap0s0jtx,
    inst_evh_2095089031469157_clickPreventStop_nvj10oer9,
    inst_evh_2095089031469157_clickPreventStop,
  };

  const evh_7315092382398562_reload_q1mknmhlm = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_q1mknmhlm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_9ap0s0jtx = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_9ap0s0jtx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_nvj10oer9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_nvj10oer9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Rental', 'General') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <ItpLabeledListGroupItem
          :label="pgettext('Rental', 'Name')"
          :content="rental.name"
          v-if="rental.name"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Rental', 'Description')"
          :content="rental.description"
          v-if="rental.description"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem :label="pgettext('Rental', 'Owner company')">
          <ItpLink
            :text="rental.company.name"
            :to="{
              name: 'app.company',
              params: { id: rental.company.id },
            }"
          >
          </ItpLink>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Rental', 'Site')"
          v-if="rental.site"
        >
          <ItpLink
            :to="{
              name: 'app.property',
              params: { id: rental.site.id },
            }"
          >
            {{ rental.site.name }}
            <span v-if="rental.site.description"
              >- {{ rental.site.description }}</span
            >
          </ItpLink>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Rental', 'Categories')"
          v-if="rental.categories && rental.categories.length"
        >
          <ItpBadge
            :key="value"
            :text="value"
            v-for="value in rental.categories"
          >
          </ItpBadge>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Rental', 'Location')"
          :content="rental.location_code"
          v-if="rental.location_code"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Rental', 'Lot number')"
          :content="rental.lot_number"
          v-if="rental.lot_number"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Rental', 'Building number')"
          :content="rental.house_number"
          v-if="rental.house_number"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Rental', 'Area')"
          v-if="rental.area"
        >
          <ItpText> {{ rental.area }} {{ rental.unit_of_area }} </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Rental', 'Net floor area')"
          v-if="rental.net_floor_area"
        >
          <ItpText>
            {{ rental.net_floor_area }} {{ rental.unit_of_area }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Rental', 'Net floor area factor')"
          v-if="rental.net_floor_area_factor"
        >
          <ItpText>
            {{ rental.net_floor_area_factor }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem :label="pgettext('Rental', 'Validity')">
          <UcRentalValidityCard :item="rental"> </UcRentalValidityCard>
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
